import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import store from '@/store';
import { setProfilePicture } from '@/api/user';
import CButton from '@/components/shared/CButton';
import CFileInput from '@/components/shared/CFileInput';
import CUserAvatar from '@/components/shared/CUserAvatar.vue';
import { useToaster } from '@/composables/toaster';
import { Roles } from '@/typings/user';
import { getImageDimensions, limitImageResolutionFromFile, uploadImageToS3 } from '@/utils/imageUtils';


export default /*#__PURE__*/_defineComponent({
  __name: 'SidebarUserCard',
  props: {
    collapsed: { type: Boolean },
    hasAccountsToImpersonate: { type: Boolean }
  },
  emits: ["impersonate", "impersonate-child"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'impersonate'): void;
    (e: 'impersonate-child'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    collapsed?: boolean;
    hasAccountsToImpersonate?: boolean;
};

const MAX_PICTURE_FILE_SIZE_MB = 5;
const MIN_PICTURE_SIZE = 200;
const MAX_PICTURE_SIZE = 512;





const toaster = useToaster();

const getInitials = (name?: string) => name?.toUpperCase().slice(0, 2);

/**
 * When the session is impersonated the card shows the user that started the impersonation,
 *  and the small badge shows the impersonated one.
 */
const userInfo = computed(() => {
    const user = store.state.user;
    const name = user?.Name || user?.Username;
    const impersonatedFrom = user?.UserImpersonatedFrom;
    const impersonatedFromName = impersonatedFrom?.Name || impersonatedFrom?.Username;
    return {
        isImpersonated: impersonatedFrom != undefined,
        fullName: impersonatedFromName || name,
        username: user?.Username,
        picturePath: impersonatedFrom ? impersonatedFrom.ProfilePicturePath : user?.ProfilePicturePath,
        impersonatedName: name,
        impersonatedInitials: getInitials(name),
        impersonatedPicturePath: user?.ProfilePicturePath
    };
});

const isImpersonateEnabled = computed(() => {
    const user = store.state.user;
    return !user?.ImpersonatedFromChildUser && (userRoles.value.has(Roles.ADMIN) || user?.ImpersonatedFromAdminUser);
});

const isSwitchTeamEnabled = computed(() => {
    const user = store.state.user;
    return (
        props.hasAccountsToImpersonate &&
        (user?.ImpersonatedFromParentUser ||
            userRoles.value.has(Roles.PARENT) ||
            user?.ImpersonatedFromChildUser ||
            userRoles.value.has(Roles.IMPERSONATE_PARENT))
    );
});

const userRoles = computed(() => {
    const user = store.state.user;
    const roles = user?.AccessRoles?.Serialized.split(',') || [];
    return new Set(roles);
});

/**
 * The endpoint updates the logged user, which is not the one displayed in the card when an
 *  admin impersonates somebody else: uploading there would silently replace the picture of
 *  the impersonated account.
 */
const canUploadPicture = computed(() => !store.state.user?.ImpersonatedFromAdminUser);

const getFileDimensions = async (file: File) => {
    const fileUrl = URL.createObjectURL(file);
    try {
        return await getImageDimensions(fileUrl);
    } finally {
        URL.revokeObjectURL(fileUrl);
    }
};

const updateUserPicture = (path: string) => {
    const user = store.state.user;
    if (!user) return;

    if (user.UserImpersonatedFrom) {
        store.commit('user', {
            ...user,
            UserImpersonatedFrom: { ...user.UserImpersonatedFrom, ProfilePicturePath: path }
        });
        return;
    }

    store.commit('user', { ...user, ProfilePicturePath: path });
};

const isUploading = ref(false);
const onPictureSelected = async (files: File[]) => {
    const [file] = files;
    if (!file || isUploading.value) return;

    if (file.size > MAX_PICTURE_FILE_SIZE_MB * 1024 * 1024) {
        toaster.error(`The profile picture must be smaller than ${MAX_PICTURE_FILE_SIZE_MB}MB.`);
        return;
    }

    isUploading.value = true;
    try {
        const { width, height } = await getFileDimensions(file);
        if (width < MIN_PICTURE_SIZE || height < MIN_PICTURE_SIZE) {
            toaster.error(`The profile picture must be at least ${MIN_PICTURE_SIZE}x${MIN_PICTURE_SIZE} pixels.`);
            return;
        }

        const { file: resizedFile } = await limitImageResolutionFromFile(file, MAX_PICTURE_SIZE, MAX_PICTURE_SIZE);

        // The resize always encodes the canvas as PNG keeping the original name, so the file is
        //  rebuilt to keep the extension, the mime type and the actual content consistent
        const pictureFile = new File([resizedFile], 'profile-picture.png', { type: 'image/png' });

        const { Path } = await uploadImageToS3('user-profile-thumbs', pictureFile);
        await setProfilePicture(Path);
        updateUserPicture(Path);
    } catch (error) {
        console.error('Failed to update the profile picture', error);
        toaster.error('The profile picture could not be updated. Please try again.');
    } finally {
        isUploading.value = false;
    }
};

return { __sfc: true,MAX_PICTURE_FILE_SIZE_MB, MIN_PICTURE_SIZE, MAX_PICTURE_SIZE, props, emit, toaster, getInitials, userInfo, isImpersonateEnabled, isSwitchTeamEnabled, userRoles, canUploadPicture, getFileDimensions, updateUserPicture, isUploading, onPictureSelected, CButton, CFileInput, CUserAvatar }
}

})